import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";

import { ApiError } from "../../api/client";
import { useMe } from "../../auth/useMe";
import { HierarchyApi, type Branch, type Constituency, type District, type Ward } from "../hierarchy/api";
import { PollingStationsApi, type PollingStation } from "./api";

/**
 * Polling-station admin. Cascading dropdowns walk the hierarchy
 * (district → constituency → ward → branch); the list and create form
 * underneath operate on the selected branch.
 */
export function PollingStations() {
  const qc = useQueryClient();
  const { isSuperadmin } = useMe();
  const [error, setError] = useState<string | null>(null);

  const [districtId, setDistrictId] = useState<string>("");
  const [constituencyId, setConstituencyId] = useState<string>("");
  const [wardId, setWardId] = useState<string>("");
  const [branchId, setBranchId] = useState<string>("");

  const districts = useQuery({ queryKey: ["districts"], queryFn: HierarchyApi.listDistricts });
  const constituencies = useQuery({
    queryKey: ["constituencies", districtId],
    queryFn: () => HierarchyApi.listConstituenciesInDistrict(districtId),
    enabled: !!districtId,
  });
  const wards = useQuery({
    queryKey: ["wards", constituencyId],
    queryFn: () => HierarchyApi.listWards(constituencyId),
    enabled: !!constituencyId,
  });
  const branches = useQuery({
    queryKey: ["branches", wardId],
    queryFn: () => HierarchyApi.listBranches(wardId),
    enabled: !!wardId,
  });
  const stations = useQuery({
    queryKey: ["polling-stations", branchId],
    queryFn: () => PollingStationsApi.listForBranch(branchId),
    enabled: !!branchId,
  });

  // Reset child selections when a parent changes.
  useEffect(() => { setConstituencyId(""); setWardId(""); setBranchId(""); }, [districtId]);
  useEffect(() => { setWardId(""); setBranchId(""); }, [constituencyId]);
  useEffect(() => { setBranchId(""); }, [wardId]);

  const create = useMutation({
    mutationFn: (body: { name: string; code: string; registeredVoters: number; lat?: number; lng?: number }) =>
      PollingStationsApi.create(branchId, { ...body, branchId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["polling-stations", branchId] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : (e as Error).message),
  });
  const remove = useMutation({
    mutationFn: PollingStationsApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["polling-stations", branchId] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : (e as Error).message),
  });

  return (
    <div>
      <header style={{ marginBottom: 8 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Polling stations</h1>
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>
          Each polling station belongs to a branch. Pick the location below to manage its stations.
        </p>
      </header>

      {error && <div role="alert" className="error">{error}</div>}

      <div className="card" style={{ marginTop: 12, marginBottom: 16 }}>
        <div className="row-3" style={{ gap: 8 }}>
          <select value={districtId} onChange={(e) => setDistrictId(e.target.value)}>
            <option value="">— Select district —</option>
            {districts.data?.items.map((d: District) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <select value={constituencyId} onChange={(e) => setConstituencyId(e.target.value)} disabled={!districtId}>
            <option value="">— Select constituency —</option>
            {constituencies.data?.items.map((c: Constituency) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select value={wardId} onChange={(e) => setWardId(e.target.value)} disabled={!constituencyId}>
            <option value="">— Select ward —</option>
            {wards.data?.items.map((w: Ward) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>
        <div style={{ marginTop: 8 }}>
          <select value={branchId} onChange={(e) => setBranchId(e.target.value)} disabled={!wardId}>
            <option value="">— Select branch —</option>
            {branches.data?.items.map((b: Branch) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
      </div>

      {branchId ? (
        <>
          <CreatePollingStationForm
            onSubmit={(v) => { setError(null); create.mutate(v); }}
            submitting={create.isPending}
          />

          {stations.isLoading && <p>Loading…</p>}

          <ul className="tree" style={{ marginTop: 16 }}>
            {stations.data?.items.map((ps: PollingStation) => (
              <li key={ps.id}>
                <div className="row">
                  <span>
                    <strong>{ps.name}</strong>{" "}
                    <span className="muted">(<code>{ps.code}</code> · {ps.registeredVoters.toLocaleString()} registered)</span>
                  </span>
                  {isSuperadmin && (
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`Delete polling station "${ps.name}"?`))
                          remove.mutate(ps.id);
                      }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </li>
            ))}
            {stations.data?.items.length === 0 && !stations.isLoading && (
              <li className="muted">No polling stations in this branch yet. Add one above.</li>
            )}
          </ul>
        </>
      ) : (
        <div className="muted" style={{ padding: 16 }}>
          Pick a district, constituency, ward, and branch to manage its polling stations.
        </div>
      )}
    </div>
  );
}

function CreatePollingStationForm({
  onSubmit, submitting,
}: {
  onSubmit: (v: { name: string; code: string; registeredVoters: number; lat?: number; lng?: number }) => void;
  submitting: boolean;
}) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [registered, setRegistered] = useState("0");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");

  function submit(e: FormEvent) {
    e.preventDefault();
    const cleanName = name.trim();
    const cleanCode = code.trim().replace(/[^A-Za-z0-9_-]/g, "").slice(0, 32) || cleanName.replace(/\s+/g, "-").replace(/[^A-Za-z0-9_-]/g, "").slice(0, 32);
    if (!cleanName || !cleanCode) return;
    onSubmit({
      name: cleanName,
      code: cleanCode,
      registeredVoters: Math.max(0, Number(registered) || 0),
      lat: lat ? Number(lat) : undefined,
      lng: lng ? Number(lng) : undefined,
    });
    setName(""); setCode(""); setRegistered("0"); setLat(""); setLng("");
  }

  return (
    <form className="create" onSubmit={submit}>
      <input
        value={name} onChange={(e) => setName(e.target.value)}
        placeholder="Polling station name"
        autoCapitalize="words"
      />
      <input
        value={code} onChange={(e) => setCode(e.target.value)}
        placeholder="Code — leave blank to auto-generate"
        spellCheck={false}
      />
      <input
        value={registered} onChange={(e) => setRegistered(e.target.value)}
        placeholder="Registered voters"
        type="number"
        min="0"
        style={{ maxWidth: 140 }}
      />
      <input
        value={lat} onChange={(e) => setLat(e.target.value)}
        placeholder="Lat"
        spellCheck={false}
        style={{ maxWidth: 100 }}
      />
      <input
        value={lng} onChange={(e) => setLng(e.target.value)}
        placeholder="Lng"
        spellCheck={false}
        style={{ maxWidth: 100 }}
      />
      <button type="submit" disabled={submitting || !name.trim()}>{submitting ? "Adding…" : "Add"}</button>
    </form>
  );
}
